import { Link } from "react-router-dom";
import { MapPin, Mail } from "lucide-react";
import Logo from "@/components/Logo";
import { useSite, FALLBACK_SETTINGS } from "@/lib/content";

export default function Footer() {
  const { data } = useSite();
  const s = data?.settings || FALLBACK_SETTINGS;
  const year = new Date().getFullYear();

  return (
    <footer className="border-t border-slate-800/70 bg-void" data-testid="site-footer">
      <div className="mx-auto grid max-w-7xl gap-12 px-6 py-16 sm:grid-cols-2 lg:grid-cols-4 lg:px-10">
        <div>
          <Logo />
          <p className="mt-5 max-w-xs text-sm leading-relaxed text-slate-400">
            LogiTech Nexus designs and builds modern software that helps businesses
            operate smarter, automate workflows, connect systems and scale with confidence.
          </p>
        </div>

        <div>
          <h3 className="font-display text-sm font-semibold uppercase tracking-widest text-slate-200">Company</h3>
          <ul className="mt-5 space-y-3 text-sm">
            <li><Link data-testid="footer-about" className="text-slate-400 transition-colors hover:text-white" to="/about">About</Link></li>
            <li><Link data-testid="footer-process" className="text-slate-400 transition-colors hover:text-white" to="/our-process">Our Process</Link></li>
            <li><Link data-testid="footer-industries" className="text-slate-400 transition-colors hover:text-white" to="/industries">Industries</Link></li>
            <li><Link data-testid="footer-contact" className="text-slate-400 transition-colors hover:text-white" to="/contact">Contact</Link></li>
          </ul>
        </div>

        <div>
          <h3 className="font-display text-sm font-semibold uppercase tracking-widest text-slate-200">Services</h3>
          <ul className="mt-5 space-y-3 text-sm">
            <li><Link data-testid="footer-software-dev" className="text-slate-400 transition-colors hover:text-white" to="/services/software-development">Software Development</Link></li>
            <li><Link data-testid="footer-web-apps" className="text-slate-400 transition-colors hover:text-white" to="/services/web-applications">Web Applications</Link></li>
            <li><Link data-testid="footer-saas" className="text-slate-400 transition-colors hover:text-white" to="/services/saas-products">SaaS Development</Link></li>
            <li><Link data-testid="footer-automation" className="text-slate-400 transition-colors hover:text-white" to="/services/workflow-automation">Automation</Link></li>
            <li><Link data-testid="footer-integrations" className="text-slate-400 transition-colors hover:text-white" to="/services/system-integrations">Integrations</Link></li>
          </ul>
        </div>

        <div>
          <h3 className="font-display text-sm font-semibold uppercase tracking-widest text-slate-200">Contact</h3>
          <address className="mt-5 space-y-4 text-sm not-italic text-slate-400">
            <p className="flex gap-3">
              <MapPin size={16} className="mt-0.5 shrink-0 text-blue-400" />
              <span>
                {s.companyName}
                <br />
                {s.addressLine1}
                <br />
                {s.addressLine2}
                <br />
                {s.addressCountry}
              </span>
            </p>
            <p>
              <a
                href={`mailto:${s.email}`}
                data-testid="footer-email"
                className="flex items-center gap-3 text-slate-400 transition-colors hover:text-white"
              >
                <Mail size={16} className="shrink-0 text-blue-400" />
                {s.email}
              </a>
            </p>
          </address>
        </div>
      </div>

      <div className="border-t border-slate-800/70">
        <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-4 px-6 py-6 text-xs text-slate-500 sm:flex-row sm:items-center lg:px-10">
          <p data-testid="footer-copyright">© {year} {s.companyName}. All rights reserved.</p>
          <div className="flex gap-6">
            <Link data-testid="footer-privacy" className="transition-colors hover:text-slate-300" to="/privacy">Privacy Policy</Link>
            <Link data-testid="footer-terms" className="transition-colors hover:text-slate-300" to="/terms">Terms of Use</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
